import { Component } from '@theme/component';
import { DialogOpenEvent } from '@theme/dialog';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * Infinite Illusion unified header utility menu: a single trigger that
 * opens a panel containing currency, search, account, and cart rows.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger
 * @property {HTMLElement} panel
 * @property {HTMLButtonElement} [currencyTrigger]
 * @property {HTMLElement} [currencyPanel]
 *
 * @extends {Component<Refs>}
 */
class IiHeaderMenuComponent extends Component {
  requiredRefs = ['trigger', 'panel'];

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;

    // Search and cart trigger their own dialog/drawer directly via
    // on:click actions (#search-modal/showDialog, #cart-drawer/toggle) —
    // closing this panel whenever either opens covers "closes on
    // selection" without needing to intercept those clicks ourselves.
    document.addEventListener(DialogOpenEvent.eventName, this.#closePanel, { signal });
    document.addEventListener(DrawerOpenEvent.eventName, this.#closePanel, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#removeOpenListeners();
  }

  get #isOpen() {
    return !this.refs.panel.hidden;
  }

  togglePanel = () => {
    if (this.#isOpen) {
      this.#closePanel();
    } else {
      this.#openPanel();
    }
  };

  #openPanel = () => {
    this.refs.panel.hidden = false;
    this.refs.trigger.setAttribute('aria-expanded', 'true');

    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onOutsideClick, { capture: true });

    requestAnimationFrame(() => {
      const firstRow = this.refs.panel.querySelector('button, a');
      if (firstRow instanceof HTMLElement) firstRow.focus();
    });
  };

  #closePanel = () => {
    if (!this.#isOpen) return;

    this.refs.panel.hidden = true;
    this.refs.trigger.setAttribute('aria-expanded', 'false');
    this.#removeOpenListeners();

    const { currencyPanel, currencyTrigger } = this.refs;
    if (currencyPanel) currencyPanel.hidden = true;
    currencyTrigger?.setAttribute('aria-expanded', 'false');
  };

  #removeOpenListeners = () => {
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('click', this.#onOutsideClick, { capture: true });
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    this.#closePanel();
    this.refs.trigger.focus();
  };

  /**
   * @param {MouseEvent} event
   */
  #onOutsideClick = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) {
      this.#closePanel();
    }
  };

  toggleCurrency = () => {
    const { currencyPanel, currencyTrigger } = this.refs;
    if (!currencyPanel || !currencyTrigger) return;

    const isOpen = !currencyPanel.hidden;
    currencyPanel.hidden = isOpen;
    currencyTrigger.setAttribute('aria-expanded', String(!isOpen));
  };
}

if (!customElements.get('ii-header-menu-component')) {
  customElements.define('ii-header-menu-component', IiHeaderMenuComponent);
}
